// Skill Effect Preloading & Caching
// Instant skill responsiveness with pre-loaded assets
// Shader warm-up, audio pre-loading, and effect template cloning

import * as THREE from 'three';
import type { SkillDefinition, DamageType } from 'shared';

// SkillId is just a string identifier
type SkillId = string;

interface CachedGeometry {
  geometry: THREE.BufferGeometry;
  refCount: number;
}

interface CachedMaterial {
  material: THREE.Material;
  refCount: number;
}

interface CachedMesh {
  mesh: THREE.Mesh | THREE.Points | THREE.Line;
  inUse: boolean;
}

interface EffectTemplate {
  geometries: THREE.BufferGeometry[];
  materials: THREE.Material[];
  createInstance: () => THREE.Object3D;
}

// Damage type to visual style mapping
const DAMAGE_TYPE_STYLES: Record<DamageType, { color: number; emissive: number; particleType: string }> = {
  physical: { color: 0xcccccc, emissive: 0x222222, particleType: 'hit_impact' },
  fire: { color: 0xff4400, emissive: 0xff2200, particleType: 'fire_trail' },
  ice: { color: 0x88ccff, emissive: 0x4488ff, particleType: 'ice_shards' },
  lightning: { color: 0x8888ff, emissive: 0x4444ff, particleType: 'lightning_spark' },
  arcane: { color: 0xaa44ff, emissive: 0x6622aa, particleType: 'arcane_burst' },
  holy: { color: 0xffffaa, emissive: 0xffff88, particleType: 'holy_light' },
  shadow: { color: 0x440066, emissive: 0x220033, particleType: 'shadow_tendrils' },
  nature: { color: 0x44ff44, emissive: 0x22aa22, particleType: 'heal_sparkle' },
};

export class SkillEffectCache {
  private scene: THREE.Scene;

  // Geometry cache
  private geometryCache: Map<string, CachedGeometry> = new Map();

  // Material cache
  private materialCache: Map<string, CachedMaterial> = new Map();

  // Mesh pools per effect type
  private meshPools: Map<string, CachedMesh[]> = new Map();

  // Effect templates
  private effectTemplates: Map<string, EffectTemplate> = new Map();

  // Audio buffer cache
  private audioBuffers: Map<string, AudioBuffer> = new Map();
  private audioContext: AudioContext | null = null;

  // Texture atlas for skill icons
  private iconAtlas: THREE.Texture | null = null;
  private iconAtlasData: Map<SkillId, { u: number; v: number; w: number; h: number }> = new Map();

  // Preload state
  private isPreloaded = false;
  private preloadProgress = 0;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  // Call during loading screen to warm up all effects
  public async preloadAll(
    skills: Map<SkillId, SkillDefinition>,
    onProgress?: (progress: number) => void
  ): Promise<void> {
    const totalSteps = skills.size * 3 + 5; // Geometries, materials, templates + base assets
    let currentStep = 0;

    const updateProgress = () => {
      currentStep++;
      this.preloadProgress = currentStep / totalSteps;
      onProgress?.(this.preloadProgress);
    };

    // 1. Create base geometries
    this.createBaseGeometries();
    updateProgress();

    // 2. Create base materials
    this.createBaseMaterials();
    updateProgress();

    // 3. Initialize audio context
    await this.initializeAudio();
    updateProgress();

    // 4. Create skill effect templates
    for (const [skillId, skill] of skills) {
      this.createEffectTemplate(skillId, skill);
      updateProgress();
    }

    // 5. Pre-warm shaders by rendering once off-screen
    await this.warmUpShaders();
    updateProgress();

    // 6. Create mesh pools
    this.createMeshPools();
    updateProgress();

    this.isPreloaded = true;
  }

  private createBaseGeometries(): void {
    // Projectile geometries
    this.cacheGeometry('projectile_sphere', new THREE.SphereGeometry(0.2, 8, 6));
    this.cacheGeometry('projectile_cone', new THREE.ConeGeometry(0.15, 0.5, 6));
    this.cacheGeometry('projectile_arrow', this.createArrowGeometry());

    // Impact geometries
    this.cacheGeometry('impact_ring', new THREE.RingGeometry(0.1, 1, 16));
    this.cacheGeometry('impact_sphere', new THREE.SphereGeometry(0.5, 12, 8));

    // AOE geometries
    this.cacheGeometry('aoe_circle', new THREE.CircleGeometry(5, 24));
    this.cacheGeometry('aoe_cone', this.createConeAOEGeometry(5, Math.PI / 4));

    // Trail geometry
    this.cacheGeometry('trail', new THREE.PlaneGeometry(1, 0.1, 10, 1));

    // Beam geometry
    this.cacheGeometry('beam', new THREE.CylinderGeometry(0.1, 0.1, 5, 8));
  }

  private createArrowGeometry(): THREE.BufferGeometry {
    const shape = new THREE.Shape();
    shape.moveTo(0, 0.3);
    shape.lineTo(0.1, 0);
    shape.lineTo(0.05, 0);
    shape.lineTo(0.05, -0.4);
    shape.lineTo(-0.05, -0.4);
    shape.lineTo(-0.05, 0);
    shape.lineTo(-0.1, 0);
    shape.closePath();

    return new THREE.ExtrudeGeometry(shape, { depth: 0.05, bevelEnabled: false });
  }

  private createConeAOEGeometry(radius: number, angle: number): THREE.BufferGeometry {
    const segments = 16;
    const vertices: number[] = [0, 0, 0]; // Origin

    for (let i = 0; i <= segments; i++) {
      const a = -angle / 2 + (angle * i) / segments;
      vertices.push(Math.sin(a) * radius, 0, Math.cos(a) * radius);
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));

    // Create indices for triangle fan
    const indices: number[] = [];
    for (let i = 1; i <= segments; i++) {
      indices.push(0, i, i + 1);
    }
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    return geometry;
  }

  private createBaseMaterials(): void {
    // Create materials for each damage type
    for (const [type, style] of Object.entries(DAMAGE_TYPE_STYLES)) {
      // Projectile material
      this.cacheMaterial(
        `projectile_${type}`,
        new THREE.MeshStandardMaterial({
          color: style.color,
          emissive: style.emissive,
          emissiveIntensity: 0.5,
          transparent: true,
          opacity: 0.9,
        })
      );

      // Impact material
      this.cacheMaterial(
        `impact_${type}`,
        new THREE.MeshBasicMaterial({
          color: style.color,
          transparent: true,
          opacity: 0.8,
          side: THREE.DoubleSide,
        })
      );

      // AOE ground material
      this.cacheMaterial(
        `aoe_${type}`,
        new THREE.MeshBasicMaterial({
          color: style.color,
          transparent: true,
          opacity: 0.4,
          side: THREE.DoubleSide,
        })
      );
    }

    // Trail material (shader)
    this.cacheMaterial(
      'trail',
      new THREE.ShaderMaterial({
        uniforms: {
          color: { value: new THREE.Color(0xffffff) },
          time: { value: 0 },
        },
        vertexShader: `
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          uniform vec3 color;
          uniform float time;
          varying vec2 vUv;
          void main() {
            float alpha = (1.0 - vUv.x) * 0.8;
            alpha *= 0.8 + 0.2 * sin(time * 10.0 + vUv.x * 5.0);
            gl_FragColor = vec4(color, alpha);
          }
        `,
        transparent: true,
        side: THREE.DoubleSide,
      })
    );
  }

  private async initializeAudio(): Promise<void> {
    try {
      this.audioContext = new AudioContext();

      // Pre-load common skill sounds
      const soundUrls = [
        'slash', 'impact', 'fire_cast', 'ice_cast', 'heal_cast',
        'lightning_strike', 'holy_smite', 'shadow_bolt'
      ];

      // Generate synthetic sounds if no actual audio files
      for (const sound of soundUrls) {
        const buffer = this.generateSynthSound(sound);
        this.audioBuffers.set(sound, buffer);
      }
    } catch (e) {
      console.warn('Audio initialization failed:', e);
    }
  }

  private generateSynthSound(type: string): AudioBuffer {
    const ctx = this.audioContext!;
    const duration = 0.3;
    const sampleRate = ctx.sampleRate;
    const buffer = ctx.createBuffer(1, duration * sampleRate, sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < data.length; i++) {
      const t = i / sampleRate;
      let sample = 0;

      switch (type) {
        case 'slash':
          sample = Math.sin(t * 400 * (1 - t * 3)) * Math.exp(-t * 10);
          break;
        case 'impact':
          sample = (Math.random() * 2 - 1) * Math.exp(-t * 15);
          break;
        case 'fire_cast':
          sample = Math.sin(t * 200) * Math.exp(-t * 5) + Math.random() * 0.3 * Math.exp(-t * 8);
          break;
        case 'ice_cast':
          sample = Math.sin(t * 800 * (1 + t)) * Math.exp(-t * 8);
          break;
        default:
          sample = Math.sin(t * 300) * Math.exp(-t * 10);
      }

      data[i] = sample * 0.5;
    }

    return buffer;
  }

  private createEffectTemplate(skillId: SkillId, skill: SkillDefinition): void {
    const damageType = skill.damageType ?? 'physical';
    const style = DAMAGE_TYPE_STYLES[damageType];

    const template: EffectTemplate = {
      geometries: [],
      materials: [],
      createInstance: () => {
        const group = new THREE.Group();

        // Projectile
        if (skill.targetType === 'enemy' || skill.targetType === 'point') {
          const projectileGeo = this.getGeometry('projectile_sphere')!;
          const projectileMat = this.getMaterial(`projectile_${damageType}`)!.clone();

          const projectile = new THREE.Mesh(projectileGeo, projectileMat);
          projectile.name = 'projectile';
          group.add(projectile);
        }

        // Impact effect
        const impactGeo = this.getGeometry('impact_ring')!;
        const impactMat = this.getMaterial(`impact_${damageType}`)!.clone();
        const impact = new THREE.Mesh(impactGeo, impactMat);
        impact.name = 'impact';
        impact.rotation.x = -Math.PI / 2;
        impact.visible = false;
        group.add(impact);

        // AOE indicator for area skills
        if (skill.aoeRadius && skill.aoeRadius > 0) {
          const aoeGeo = this.getGeometry('aoe_circle')!;
          const aoeMat = this.getMaterial(`aoe_${damageType}`)!.clone();
          const aoe = new THREE.Mesh(aoeGeo, aoeMat);
          aoe.name = 'aoe';
          aoe.rotation.x = -Math.PI / 2;
          aoe.scale.setScalar(skill.aoeRadius);
          group.add(aoe);
        }

        return group;
      },
    };

    this.effectTemplates.set(skillId, template);
  }

  private async warmUpShaders(): Promise<void> {
    // Create a small off-screen render target
    const renderer = new THREE.WebGLRenderer({ canvas: document.createElement('canvas') });
    renderer.setSize(64, 64);

    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
    camera.position.z = 5;

    const warmupScene = new THREE.Scene();

    // Add one mesh with each material to force shader compilation
    for (const [_, cached] of this.materialCache) {
      const geometry = new THREE.SphereGeometry(0.5);
      const mesh = new THREE.Mesh(geometry, cached.material);
      warmupScene.add(mesh);
    }

    // Render once to compile shaders
    renderer.render(warmupScene, camera);

    // Cleanup
    renderer.dispose();
  }

  private createMeshPools(): void {
    // Create pools for frequently used effect meshes
    const poolConfigs = [
      { name: 'projectile', count: 20 },
      { name: 'impact', count: 30 },
      { name: 'aoe', count: 10 },
    ];

    for (const config of poolConfigs) {
      const pool: CachedMesh[] = [];
      const geometry = this.getGeometry(`${config.name}_sphere`) ?? this.getGeometry('impact_ring');

      if (geometry) {
        for (let i = 0; i < config.count; i++) {
          const material = new THREE.MeshBasicMaterial({ transparent: true });
          const mesh = new THREE.Mesh(geometry, material);
          mesh.visible = false;
          this.scene.add(mesh);
          pool.push({ mesh, inUse: false });
        }
      }

      this.meshPools.set(config.name, pool);
    }
  }

  // Public API

  public cacheGeometry(id: string, geometry: THREE.BufferGeometry): void {
    if (this.geometryCache.has(id)) {
      this.geometryCache.get(id)!.refCount++;
    } else {
      this.geometryCache.set(id, { geometry, refCount: 1 });
    }
  }

  public getGeometry(id: string): THREE.BufferGeometry | null {
    return this.geometryCache.get(id)?.geometry ?? null;
  }

  public cacheMaterial(id: string, material: THREE.Material): void {
    if (this.materialCache.has(id)) {
      this.materialCache.get(id)!.refCount++;
    } else {
      this.materialCache.set(id, { material, refCount: 1 });
    }
  }

  public getMaterial(id: string): THREE.Material | null {
    return this.materialCache.get(id)?.material ?? null;
  }

  public getEffectTemplate(skillId: SkillId): EffectTemplate | null {
    return this.effectTemplates.get(skillId) ?? null;
  }

  public createSkillEffect(skillId: SkillId): THREE.Object3D | null {
    const template = this.effectTemplates.get(skillId);
    if (!template) return null;
    return template.createInstance();
  }

  public getMeshFromPool(poolName: string): THREE.Mesh | null {
    const pool = this.meshPools.get(poolName);
    if (!pool) return null;

    for (const cached of pool) {
      if (!cached.inUse) {
        cached.inUse = true;
        cached.mesh.visible = true;
        return cached.mesh as THREE.Mesh;
      }
    }

    return null; // Pool exhausted
  }

  public returnMeshToPool(mesh: THREE.Mesh): void {
    for (const pool of this.meshPools.values()) {
      const cached = pool.find(c => c.mesh === mesh);
      if (cached) {
        cached.inUse = false;
        cached.mesh.visible = false;
        return;
      }
    }
  }

  public playSound(soundId: string, volume: number = 1): void {
    if (!this.audioContext || !this.audioBuffers.has(soundId)) return;

    const source = this.audioContext.createBufferSource();
    source.buffer = this.audioBuffers.get(soundId)!;

    const gainNode = this.audioContext.createGain();
    gainNode.gain.value = volume;

    source.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    source.start();
  }

  public isReady(): boolean {
    return this.isPreloaded;
  }

  public getPreloadProgress(): number {
    return this.preloadProgress;
  }

  public destroy(): void {
    // Dispose geometries
    for (const cached of this.geometryCache.values()) {
      cached.geometry.dispose();
    }
    this.geometryCache.clear();

    // Dispose materials
    for (const cached of this.materialCache.values()) {
      cached.material.dispose();
    }
    this.materialCache.clear();

    // Remove pooled meshes
    for (const pool of this.meshPools.values()) {
      for (const cached of pool) {
        this.scene.remove(cached.mesh);
        (cached.mesh.material as THREE.Material).dispose();
      }
    }
    this.meshPools.clear();

    // Close audio context
    this.audioContext?.close();
  }
}
